/**
 * Application Entry Point
 *
 * Features:
 * - React 18 with createRoot API
 * - TanStack Query for server state management with optimized caching
 * - React Query DevTools for development (automatically excluded in production)
 * - Cache monitoring and performance tracking
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from './App';
import './index.css';

// ============================================================
// REACT QUERY CONFIGURATION
// Optimized defaults for better caching and performance
// ============================================================
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data considered fresh for 5 minutes (won't refetch during this time)
      staleTime: 5 * 60 * 1000, // 5 minutes

      // Keep unused data in cache for 10 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes

      // Don't refetch when user switches browser tabs (improves performance)
      refetchOnWindowFocus: false,

      // Refetch when internet connection is restored
      refetchOnReconnect: true,

      // Retry failed requests 2 times before showing error
      retry: 2,

      // Exponential backoff for retries: 1s, 2s, 4s...
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Show previous data while fetching new data (smoother UX)
      keepPreviousData: true,
    },
  },
});

// ============================================================
// CACHE MONITORING (Development Only)
// Logs cache hits/misses for debugging and optimization
// ============================================================
if (process.env.NODE_ENV === 'development') {
  const cache = queryClient.getQueryCache();

  // Track cache statistics
  let stats = { hits: 0, misses: 0 };

  cache.subscribe((event) => {
    if (!event?.query?.queryKey) return;

    const queryKey = JSON.stringify(event.query.queryKey);

    // Track fetch events
    if (event.type === 'updated') {
      const { fetchStatus, status } = event.query.state;

      if (fetchStatus === 'fetching') {
        stats.misses++;
        console.log(`🌐 [Cache Miss] Fetching: ${queryKey}`);
      } else if (fetchStatus === 'idle' && status === 'success') {
        // Data came from cache
        if (event.action?.type === 'success' && !event.action?.data) {
          stats.hits++;
          console.log(`✅ [Cache Hit] Using cached: ${queryKey}`);
        }
      }
    }
  });

  // Log cache stats every 30 seconds (development only)
  setInterval(() => {
    const total = stats.hits + stats.misses;
    if (total > 0) {
      const hitRate = ((stats.hits / total) * 100).toFixed(1);
      console.log(`📊 [Cache Stats] Hits: ${stats.hits}, Misses: ${stats.misses}, Hit Rate: ${hitRate}%`);
    }
  }, 30000);
}

// ============================================================
// RENDER APPLICATION
// ============================================================
const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {/* React Query DevTools - Only visible in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools
          initialIsOpen={false}
          position="bottom-right"
          toggleButtonProps={{
            style: {
              marginBottom: '60px', // Avoid overlap with other UI elements
              backgroundColor: '#F7971E',
            },
          }}
        />
      )}
    </QueryClientProvider>
  </React.StrictMode>
);
